import { Package, CheckCircle2, Circle } from "lucide-react";
import { getImageUrl } from "../../utils/imageUtils";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");

// Map the order status enum onto the customer-facing 3-step timeline.
const STEPS = ["Order Placed", "Order Submitted", "Order Shipped"];
const reached = (status) => {
  const s = (status || "").toLowerCase();
  if (["shipped", "delivered"].includes(s)) return 3;
  if (["processing", "submitted", "confirmed"].includes(s)) return 2;
  return 1; // pending / placed
};

const addrLine = (a) =>
  a ? [a.line1, a.line2, a.city, a.state, a.postal_code, a.country].filter(Boolean).join(", ") : "";

/**
 * Shared order list for the customer site — renders the enriched order details
 * (TR-8 2.3): buyer name/phone/email, shipping address, per-item chosen image,
 * plus the Order Placed → Submitted → Shipped timeline. Used by Home + Orders.
 */
export default function InfinityOrderList({ orders = [], loading }) {
  if (loading) return <p className="text-center text-slate-400 py-12">Loading…</p>;
  if (orders.length === 0) return <p className="text-center text-slate-400 py-12">No orders yet.</p>;

  return (
    <div className="space-y-5">
      {orders.map((o) => {
        const step = reached(o.status);
        return (
          <div key={o.order_id} id={`order-${o.order_id}`} className="card-elevated p-5 space-y-4 scroll-mt-24 target:ring-2"
            style={{ ["--tw-ring-color"]: "#2E72BC" }}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="font-semibold text-slate-800">{o.order_number}</p>
                <p className="text-xs text-slate-500">
                  Placed by {o.placed_by === "funeral_home" ? "Funeral Home" : "Family"} · {fmt(o.created_at)}
                </p>
              </div>
              <p className="font-bold text-slate-900">{money(o.total)}</p>
            </div>

            {/* Buyer + shipping address (2.3.1 / 2.3.2) */}
            <div className="grid sm:grid-cols-2 gap-3 text-sm bg-slate-50 rounded-lg p-3">
              <div>
                <p className="text-xs text-slate-400 mb-0.5">Ordered by</p>
                <p className="text-slate-700">{o.customer_name || "—"}</p>
                {o.customer_email || o.contact_email ? <p className="text-slate-500 text-xs">{o.customer_email || o.contact_email}</p> : null}
                {o.contact_phone ? <p className="text-slate-500 text-xs">{o.contact_phone}</p> : null}
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-0.5">Ship to</p>
                <p className="text-slate-700">{o.shipping_address?.name || "—"}</p>
                {addrLine(o.shipping_address) ? <p className="text-slate-500 text-xs">{addrLine(o.shipping_address)}</p> : null}
              </div>
            </div>

            {/* Items with per-item chosen image (2.3.3) */}
            <div className="space-y-2">
              {(o.items || []).map((it, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded bg-slate-50 overflow-hidden flex items-center justify-center shrink-0">
                    {it.image_url ? <img src={getImageUrl(it.image_url)} alt={it.name} className="w-full h-full object-contain" /> : <Package className="w-4 h-4 text-slate-300" />}
                  </div>
                  <div className="flex-1 text-sm">
                    <p className="text-slate-800">{it.name} × {it.quantity}</p>
                    {it.color ? <p className="text-xs text-slate-500">Color: {it.color}</p> : null}
                    {it.engraving_lines?.length ? <p className="text-xs text-slate-500">Engraving: {it.engraving_lines.join(" / ")}</p> : null}
                    {it.ash_chamber ? <p className="text-xs text-slate-500">+ Ash chamber</p> : null}
                  </div>
                  {it.custom_image_url ? (
                    <a href={getImageUrl(it.custom_image_url)} target="_blank" rel="noreferrer" title="Attached print image"
                      className="w-12 h-12 rounded border overflow-hidden shrink-0">
                      <img src={getImageUrl(it.custom_image_url)} alt="Attached print" className="w-full h-full object-cover" />
                    </a>
                  ) : null}
                  <p className="text-sm text-slate-600 w-16 text-right">{money(it.display_price)}</p>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2 pt-2 border-t">
              {STEPS.map((label, i) => (
                <div key={label} className="flex-1 flex items-center gap-2">
                  {i + 1 <= step ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <Circle className="w-5 h-5 text-slate-300" />}
                  <span className={`text-xs ${i + 1 <= step ? "text-slate-700 font-medium" : "text-slate-400"}`}>{label}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
